import Vue from 'vue';
import i18n from '../localization/i18n';
import VueRouter from 'vue-router';
import store from '@/store';
import Slug from '@/models/slug';
import axios from 'redaxios';
import routes from './routes';

Vue.use(VueRouter);

const allowedLanguages = ['de', 'en']; // Allowed languages for router check (else defaulting to de)

const languagePathRegEx = new RegExp(`^/(${allowedLanguages.join('|')})/`); // RegEx to check for allowed languages in path

const defaultDescription = 'Standortsuche, Förderkompass und Wissenswertes für Unternehmen in Niederösterreich';

/**
 * @param {import('vue-router').Route} to
 * @returns {string}
 */
function getDescription(to) {
  if (!to.meta.description) {
    return defaultDescription;
  }
  if (typeof to.meta.description === 'function') {
    return to.meta.description(to.params);
  }
  return to.meta.description;
}

/**
 * @param {import('vue-router').Route} to
 */
async function setTitleAndDescription(to) {
  let title = to.meta.title;
  let description = getDescription(to);
  if (to.meta.slugTitle !== false) {
    const slugItem = await getSlugForDestinationPath(router.resolve(to).resolved.path);
    if (slugItem) {
      title = slugItem.title;
      description = slugItem.description;
    }
  }
  document.querySelector('meta[name="description"]').setAttribute('content', description);
  document.title = title;
}

const router = new VueRouter({
  mode: 'history',
  routes
});

function hasQueryParams(route) {
  return !!Object.keys(route.query).length;
}

/**
 * always keep barrierefrei in route
 * clean up immo fav and property state
 */
router.beforeEach(async (to, from, next) => {
  if (router.match(to).matched.length === 0) {
    await addSlugRoute(to.path);
    const route = router.match(to).matched.length > 0 ? router.resolve(to).location : `/${i18n.locale}/home`;
    return next(route);
  }
  if (
    to.name == 'standortsuche' &&
    from.name != 'detailansicht' &&
    from.name != 'standortsuche' &&
    from.name != 'edit-objekt'
  ) {
    store.dispatch('onlyProperties', false);
    store.dispatch('favsOnly', false);
  }

  if (!hasQueryParams(to) && hasQueryParams(from) && from.query.barrierefrei && to.path !== from.path) {
    const newQuery = Object.assign({}, to.query, { barrierefrei: true });
    return next({ name: to.name, query: newQuery });
  }

  if (to.name == 'meineimmobilien') {
    return next({ path: '/' + to.params.lang + '/standortsuche', query: { ...to.query, onlyProperties: 'true' } });
  }

  if (!store.state.userdata && to.meta?.requiresAuth) {
    await store.dispatch('fetchUserdata', null);
  }

  // Language Switcher
  if (!allowedLanguages.includes(to.params.lang)) {
    to.params.lang = allowedLanguages[0];
    return next({ name: to.name, params: to.params, query: to.query });
  }
  if (to.params.lang !== i18n.locale) {
    i18n.locale = to.params.lang;
  }

  if (to.matched.some(record => record.meta.requiresAuth)) {
    const errNext = { path: `/${i18n.locale}/login`, query: { next: location.pathname } };
    await setTitleAndDescription(to);
    return next(store.state.userdata.userlevel > 0 ? undefined : errNext);
  } else {
    await setTitleAndDescription(to);
    return next();
  }
});

/**
 * @param {string} slugPath
 * @returns {Promise<Slug>}
 */
async function getSlugForSlugPath(slugPath) {
  await axios.get('/api/slug').then(({ data }) => Slug.insertOrUpdate({ data }));
  return Slug.query().where('slug', slugPath).first();
}

/**
 * @param {string} destinationPath
 * @returns {Promise<Slug>}
 */
async function getSlugForDestinationPath(destinationPath) {
  await axios.get('/api/slug').then(({ data }) => Slug.insertOrUpdate({ data }));
  return Slug.query()
    .where('path', value => value === destinationPath || `/${i18n.locale}${value}` === destinationPath)
    .first();
}

async function addSlugRoute(slugPath) {
  const slugItem = await getSlugForSlugPath(slugPath);
  if (!slugItem) {
    return;
  }

  const slug = slugItem.slug.startsWith('/') ? slugItem.slug : `/${slugItem.slug}`;
  const destinationPath = slugItem.path;
  const alias = languagePathRegEx.test(destinationPath) ? destinationPath : `/${i18n.locale}${destinationPath}`;
  router.addRoute({
    alias: slug,
    path: alias,
    props: router.resolve(alias).route.params,
    component: router.getMatchedComponents(alias)[0],
    meta: {
      ...router.match(alias)?.meta,
      title: slugItem.title
    }
  });
}

export default router;
